
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

const ProfitMarginCalculator = () => {
  const [revenue, setRevenue] = useState<string>('1000000');
  const [totalCosts, setTotalCosts] = useState<string>('750000');
  const [result, setResult] = useState<{
    profit: number;
    profitMargin: number;
    markup: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const calculateProfitMargin = () => {
    try {
      setError(null);
      
      const rev = parseFloat(revenue);
      const costs = parseFloat(totalCosts);

      if (isNaN(rev) || isNaN(costs)) {
        setError("Please enter valid numbers");
        return;
      }

      if (rev <= 0) {
        setError("Revenue must be positive");
        return;
      }

      if (costs < 0) {
        setError("Total costs cannot be negative");
        return;
      }

      // Profit = Revenue - Total Costs
      const profit = rev - costs;
      
      // Profit Margin = (Profit / Revenue) × 100
      const profitMargin = (profit / rev) * 100;
      
      // Markup = (Profit / Total Costs) × 100
      const markup = costs > 0 ? (profit / costs) * 100 : 0;

      setResult({
        profit,
        profitMargin,
        markup
      });

    } catch (err) {
      setError("Error calculating profit margin");
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getProfitMarginRating = (margin: number) => {
    if (margin >= 20) return { rating: "Excellent", color: "text-green-600" };
    if (margin >= 10) return { rating: "Good", color: "text-blue-600" };
    if (margin >= 5) return { rating: "Average", color: "text-yellow-600" };
    if (margin >= 0) return { rating: "Low", color: "text-orange-600" };
    return { rating: "Loss", color: "text-red-600" };
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold mb-4">Profit Margin Calculator</h2>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="revenue">Total Revenue ($)</Label>
              <Input
                id="revenue"
                type="number"
                value={revenue}
                onChange={(e) => setRevenue(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="total-costs">Total Costs ($)</Label>
              <Input
                id="total-costs"
                type="number"
                value={totalCosts}
                onChange={(e) => setTotalCosts(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Include all costs: COGS, operating expenses, taxes, etc.
              </p>
            </div>

            <Button onClick={calculateProfitMargin} className="w-full">
              Calculate Profit Margin
            </Button>

            {error && (
              <div className="p-3 bg-red-100 text-red-700 rounded-md text-sm">
                {error}
              </div>
            )}

            {result !== null && (
              <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                <h3 className="font-medium">Results</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Net Profit</p>
                    <p className={`text-xl font-bold ${result.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(result.profit)}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-600">Profit Margin</p>
                    <p className={`text-xl font-bold ${result.profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {result.profitMargin.toFixed(2)}%
                    </p>
                    {(() => {
                      const rating = getProfitMarginRating(result.profitMargin);
                      return (
                        <p className={`text-sm font-medium ${rating.color}`}>
                          {rating.rating}
                        </p>
                      );
                    })()}
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-600">Markup</p>
                    <p className="text-xl font-bold text-blue-600">
                      {result.markup.toFixed(2)}%
                    </p>
                  </div>
                </div>

                <div className="mt-4 text-sm">
                  <p><strong>Profit Margin Guidelines:</strong></p>
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    <li>&ge;20%: Excellent - Very profitable business</li>
                    <li>10-19%: Good - Healthy profit margin</li>
                    <li>5-9%: Average - Industry dependent</li>
                    <li>0-4%: Low - Consider cost reduction</li>
                    <li>&lt;0%: Loss - Business is losing money</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfitMarginCalculator;
